import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { InsuranceCarrier, useInsuranceCarriers } from '@/models/insuranceCarrier'
import { CarrierFormDialog } from './CarrierFormDialog'
import { MergeCarriersDialog } from './MergeCarriersDialog'

/** The Carriers tab: the insurers Cai places business with. */
export function CarrierList() {
  const { carriers, isLoading } = useInsuranceCarriers()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<InsuranceCarrier | null>(null)
  const [mergeOpen, setMergeOpen] = useState(false)

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return carriers
    return carriers.filter(
      (carrier) =>
        carrier.name.toLowerCase().includes(query) || carrier.naicCode.toLowerCase().includes(query)
    )
  }, [carriers, search])

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(carrier: InsuranceCarrier) {
    setEditing(carrier)
    setDialogOpen(true)
  }

  return (
    <div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <Input
            placeholder="Search carriers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Button onClick={openCreate}>
            <Plus className="mr-1 size-4" /> Add carrier
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <span role="status" className="sr-only">
              Loading carriers
            </span>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>NAIC</TableHead>
                <TableHead>Primary email</TableHead>
                <TableHead>Primary phone</TableHead>
                <TableHead className="text-right">Contacts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableSkeletonRows columns={5} />
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground text-center">
                    No carriers yet.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((carrier) => (
                  <TableRow key={carrier.id} className="cursor-pointer" onClick={() => openEdit(carrier)}>
                    <TableCell className="flex items-center gap-2 font-medium">
                      {carrier.logo && (
                        <img src={carrier.logo} alt="" className="size-6 rounded object-contain" />
                      )}
                      {carrier.name}
                    </TableCell>
                    <TableCell>{carrier.naicCode || '—'}</TableCell>
                    <TableCell>{carrier.primaryEmail || '—'}</TableCell>
                    <TableCell>{carrier.primaryPhone || '—'}</TableCell>
                    <TableCell className="text-right">{carrier.contacts.length}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div className="mt-4">
        <Button variant="outline" onClick={() => setMergeOpen(true)}>
          Merge carriers
        </Button>
      </div>
      <CarrierFormDialog open={dialogOpen} onOpenChange={setDialogOpen} carrier={editing} />
      <MergeCarriersDialog open={mergeOpen} onOpenChange={setMergeOpen} />
    </div>
  )
}
